import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LuArrowRight, LuCheck, LuHeart, LuLock } from 'react-icons/lu';
import { COPY } from '../../contracts/copyMap';
import { track } from '../../analytics/track';
import { CONVERSION_EVENTS } from '../../analytics/conversion';
import useStartTrial from '../../hooks/useStartTrial';
import AurumButton from './AurumButton';
import GhostButton from './GhostButton';
import UnlockProgress from './UnlockProgress';
import logoPro from '../../assets/logo-pro.webp';

// The sealed-reading gate — the Pro upsell.
//
// Register matters here more than anywhere else in the product: the analysis
// is FINISHED and READY, never "failed" and never "locked out". The work is
// done — the door is what is closed. Copy lives in COPY.UNLOCK_GATE_*.
//
// Two paths, and unlike the previous revision they are NOT presented as
// equals: the priced path is a flagged, foil-swept plate and the judging path
// is a plain slate below an "OR EARN IT" rule. That asymmetry is the whole
// point of the redesign. The judging path is still a real button, still free,
// and still the reason this design is defensible at all.
//
//   ┌─ open ────────────────────────────────────────────────────────────┐
//   │  done >= required ?                                               │
//   │      yes → cleared panel (celebration, single CTA)                │
//   │      no  → PRO plate  ──BEGIN FREE TRIAL──▶ Stripe checkout       │
//   │           OR EARN IT                                              │
//   │           EARN slate ──JUDGE TEN PORTRAITS──▶ onJudge()           │
//   └───────────────────────────────────────────────────────────────────┘
//
// No spinner and no progress bar (DESIGN.md §16.2), no red (DESIGN.md §2.1).
// Every decorative animation is behind `motion-safe:`.
//
// The priced plate carries ONE price and no offer theatre: no struck-through
// anchor, no discount badge, no countdown. See the comment above
// COPY.UNLOCK_GATE_PRO_EYEBROW for why each was removed — and for the one
// conflict still open (the displayed price vs. the charged price) that must be
// settled before this modal is shown to a real user.
export default function UnlockGateModal({
  open,
  unlock,
  onJudge,
  onDismiss,
  onViewResults,
  // Names the artifact being withheld. Defaults to the analysis because that
  // is what every judging surface gates; the routine paywall overrides it, and
  // must, since the analysis there is often already open.
  headline = COPY.UNLOCK_GATE_HEADLINE,
  // Membership-only surfaces pass showJudgePath={false}. The routine is the
  // one that matters: CLAUDE.md rule 2 — "the routine is membership-only on
  // BOTH paths; judging opens the verdict, never the routine" — and
  // getRoutine gates on requireEntitlement("routine") with no judging path
  // behind it. Offering "judge ten and this opens free" there would promise
  // something the backend will never honour.
  showJudgePath = true,
  subcopy,
  // Names WHERE this gate opened (create / routine / vault_entry) in the
  // conversion events below — the same modal fronts three pages, and an
  // unattributed paywall_viewed answers nothing.
  surface = 'unknown',
}) {
  // Shared with RoutinePaywallPanel — see useStartTrial for why
  // ALREADY_SUBSCRIBED reloads and why `submitting` stays set on success.
  // The surface suffix flows into trial_cta_clicked and the console.error.
  const { startTrial, submitting, error } = useStartTrial(`UnlockGateModal:${surface}`);

  const done = Number(unlock?.done) || 0;
  const required = Number(unlock?.required) || 10;
  const started = done > 0;
  // Without a judging path there is no debt to clear, so the celebration state
  // is unreachable — a membership surface opens by payment or not at all.
  const cleared = showJudgePath && done >= required;
  const remaining = Math.max(required - done, 0);

  // One paywall_viewed per OPEN, not per mount — the modal stays mounted
  // closed, and reopening it is a fresh look at the offer. Deliberately keyed
  // on `open` alone: prop drift while it sits open is not another view.
  useEffect(() => {
    if (!open) return;
    track(CONVERSION_EVENTS.PAYWALL_VIEWED, {
      surface,
      gate: 'unlock_gate',
      show_judge_path: showJudgePath,
      cleared,
      judgments_done: done,
      judgments_required: required,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleJudge = () => {
    track(CONVERSION_EVENTS.JUDGE_PATH_CLICKED, {
      surface,
      judgments_done: done,
      judgments_remaining: remaining,
    });
    onJudge?.();
  };

  const handleDismiss = () => {
    track(CONVERSION_EVENTS.PAYWALL_DISMISSED, { surface, gate: 'unlock_gate', cleared });
    onDismiss?.();
  };

  // Escape closes, matching the backdrop. Bound only while open so the modal
  // never eats Escape from whatever is underneath it.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onDismiss, surface]);

  const remainLabel =
    remaining === 1
      ? COPY.UNLOCK_GATE_EARN_REMAIN_ONE
      : `${remaining} ${COPY.UNLOCK_GATE_EARN_REMAIN_MANY}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-obsidian/[0.88] p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
          role="dialog"
          aria-modal="true"
          aria-label={headline}
          data-testid="unlock-gate"
          onClick={handleDismiss}
        >
          <motion.div
            className="relative w-[560px] max-w-full max-h-[94vh] overflow-y-auto scrollbar-hide rounded-modal border border-aurum/30 bg-obsidian-raised shadow-[0_50px_120px_-24px_rgba(0,0,0,0.95),0_0_60px_rgba(201,164,92,0.10)]"
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.38, ease: [0.34, 1.16, 0.64, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ambient aura, foil sweep and drifting motes. Purely decorative. */}
            <div aria-hidden="true">
              <div className="pointer-events-none absolute left-1/2 -top-[60px] h-[260px] w-[420px] -translate-x-1/2 bg-aurum-halo motion-safe:animate-[aura-pulse_4.5s_ease-in-out_infinite]" />
              <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-modal">
                <div className="absolute inset-y-0 w-[26%] bg-foil-band-faint motion-safe:animate-[foil-sweep_5.5s_ease-in-out_infinite]" />
              </div>
              <div className="pointer-events-none absolute left-[34px] top-[92px] h-[5px] w-[5px] bg-aurum-bright motion-safe:animate-[twinkle_3.2s_ease-in-out_infinite]" />
              <div className="pointer-events-none absolute right-[46px] top-[68px] h-1 w-1 bg-aurum-bright motion-safe:animate-[twinkle_2.6s_ease-in-out_0.7s_infinite]" />
              <div className="pointer-events-none absolute right-[30px] top-[168px] h-[5px] w-[5px] bg-aurum motion-safe:animate-[twinkle_3.8s_ease-in-out_1.4s_infinite]" />
              <div className="pointer-events-none absolute left-[52px] top-[210px] h-1 w-1 bg-aurum motion-safe:animate-[twinkle_3s_ease-in-out_2.1s_infinite]" />
            </div>

            {/* The design is drawn desktop-only at 560px. Below `sm` the panel
                is full-bleed, so the gutters, the display sizes and the two
                card headers step down — see the flag and the header rows. */}
            <div className="relative px-6 pb-[26px] pt-8 sm:px-[38px]">
              {/* Brand lockup. No Censor Bar eyebrow: the design carried an
                  ANALYSIS SEALED / ANALYSIS OPEN bar here, but naming the seal
                  above the offer frames the moment as a denial. The lockup and
                  the headline carry it instead. The cleared state keeps its own
                  ANALYSIS UNLOCKED eyebrow inside ClearedPanel, where it is a
                  reward rather than a label. */}
              <div className="text-center">
                <img
                  src={logoPro}
                  alt="MOGGED PRO"
                  className="mx-auto block h-auto w-60 max-w-[72%] mix-blend-screen"
                />
                <h2 className="m-0 mt-[18px] font-display text-[28px] font-light tracking-[0.02em] text-bone sm:text-[34px]">
                  {headline}
                </h2>
                <p className="mx-auto m-0 mt-[10px] max-w-[400px] font-display text-[16.5px] leading-[1.55] text-bone-dim">
                  {cleared ? COPY.UNLOCK_GATE_BODY_OPEN : (subcopy || COPY.UNLOCK_GATE_BODY)}
                </p>
              </div>

              {cleared ? (
                <ClearedPanel onViewResults={onViewResults || handleDismiss} />
              ) : (
                <>
                  {/* ── PRO path ─────────────────────────────────────────── */}
                  <div className="relative mt-[26px] overflow-hidden rounded-card border border-aurum/50 bg-aurum-faint p-[22px]">
                    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
                      <div className="absolute inset-y-0 w-[30%] bg-foil-band-soft motion-safe:animate-[foil-sweep_4.2s_ease-in-out_infinite]" />
                    </div>

                    {/* Corner ribbon on desktop. Narrow, it would eat the
                        title's line box, so it detaches into a chip above. */}
                    <div className="relative mb-3 inline-flex rounded-full bg-aurum-plate px-3 py-[5px] sm:absolute sm:right-0 sm:top-0 sm:mb-0 sm:rounded-none sm:rounded-bl-thumb sm:px-[14px]">
                      <span className="font-censor text-[9.5px] font-semibold uppercase tracking-[0.2em] text-obsidian">
                        {COPY.UNLOCK_GATE_PRO_FLAG}
                      </span>
                    </div>

                    <div className="relative flex flex-wrap items-end justify-between gap-x-4 gap-y-3 sm:flex-nowrap sm:pr-24">
                      <div>
                        <div className="font-display text-[11px] uppercase tracking-[0.24em] text-aurum-bright">
                          {COPY.UNLOCK_GATE_PRO_EYEBROW}
                        </div>
                        <div className="mt-[5px] font-display text-[22px] text-bone">
                          {COPY.UNLOCK_GATE_PRO_TITLE}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        {/* One price, unadorned. No struck-through anchor and
                            no discount badge under it — see the comment above
                            COPY.UNLOCK_GATE_PRO_EYEBROW. */}
                        <div className="flex items-baseline justify-end gap-[3px]">
                          <span
                            className="font-censor text-[34px] font-semibold leading-none text-aurum-pale tabular-nums"
                            data-testid="unlock-price"
                          >
                            {COPY.UNLOCK_GATE_PRICE}
                          </span>
                          <span className="font-display text-[13px] text-bone-dim">
                            {COPY.UNLOCK_GATE_PRICE_PERIOD}
                          </span>
                        </div>
                      </div>
                    </div>

                    <ul className="relative m-0 mt-[18px] flex list-none flex-col gap-[9px] p-0">
                      {COPY.UNLOCK_GATE_PRO_BENEFITS.map((benefit) => (
                        <li key={benefit} className="flex items-start gap-[10px]">
                          <LuCheck
                            className="mt-[3px] shrink-0 text-aurum"
                            size={15}
                            strokeWidth={2}
                            aria-hidden="true"
                          />
                          <span className="font-display text-[15.5px] leading-[1.4] text-marble">
                            {benefit}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <div className="relative mt-5">
                      <AurumButton
                        variant="plate"
                        size="lg"
                        fullWidth
                        onClick={startTrial}
                        loading={submitting}
                        trailingIcon={<LuArrowRight size={17} />}
                        data-testid="unlock-trial"
                      >
                        {COPY.UNLOCK_GATE_TRIAL_CTA}
                      </AurumButton>
                    </div>

                    <div className="relative mt-[11px] text-center font-display text-[13.5px] text-bone-faint">
                      {COPY.UNLOCK_GATE_TRIAL_TERMS}
                    </div>
                  </div>

                  {showJudgePath && (
                  <>
                  {/* ── OR ───────────────────────────────────────────────── */}
                  <div className="mt-[22px] flex items-center gap-4">
                    <div className="h-px flex-1 bg-white/[0.09]" />
                    <span className="font-display text-[11px] uppercase tracking-[0.26em] text-bone-faint">
                      {COPY.UNLOCK_GATE_OR}
                    </span>
                    <div className="h-px flex-1 bg-white/[0.09]" />
                  </div>

                  {/* ── EARN path ────────────────────────────────────────── */}
                  <div className="mt-5 rounded-card border border-hairline-soft bg-ash p-[22px]">
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
                      <div>
                        <div className="font-display text-[11px] uppercase tracking-[0.24em] text-bone-dim">
                          {COPY.UNLOCK_GATE_EARN_EYEBROW}
                        </div>
                        <div className="mt-[5px] font-display text-[20px] text-bone">
                          {COPY.UNLOCK_GATE_EARN_TITLE}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div
                          className="flex items-baseline justify-end gap-1"
                          data-testid="unlock-progress"
                        >
                          <span className="font-censor text-[30px] font-semibold leading-none text-aurum-bright tabular-nums">
                            {String(done).padStart(2, '0')}
                          </span>
                          <span className="font-censor text-[15px] text-bone-faint tabular-nums">
                            {`/ ${required}`}
                          </span>
                        </div>
                        <div className="mt-[3px] font-display text-[10.5px] uppercase tracking-[0.16em] text-bone-dim">
                          {COPY.UNLOCK_GATE_EARN_COUNT_LABEL}
                        </div>
                      </div>
                    </div>

                    {/* Extracted to UnlockProgress so Explore and Home's
                        sealed hero draw the identical row rather than each
                        re-rolling one. The numerals above stay here — this
                        modal is the only surface that right-aligns them
                        against the copy block. */}
                    <UnlockProgress
                      done={done}
                      required={required}
                      className="mt-4"
                      label={COPY.UNLOCK_GATE_EARN_COUNT_LABEL}
                    />

                    <p className="m-0 mt-[14px] font-display text-[15px] text-bone-dim">
                      {`${COPY.UNLOCK_GATE_EARN_PREFIX} `}
                      <span className="text-marble">{remainLabel}</span>
                      {` ${COPY.UNLOCK_GATE_EARN_SUFFIX}`}
                    </p>

                    <div className="mt-4">
                      <AurumButton
                        fullWidth
                        onClick={handleJudge}
                        leadingIcon={<LuHeart size={16} />}
                        data-testid="unlock-judge"
                      >
                        {started ? COPY.UNLOCK_GATE_RESUME_CTA : COPY.UNLOCK_GATE_JUDGE_CTA}
                      </AurumButton>
                    </div>
                  </div>
                  </>
                  )}
                </>
              )}

              {/* Bone, never red — DESIGN.md forbids red even for errors. */}
              {error && (
                <p className="m-0 mt-4 text-center font-display text-sm text-bone" role="alert">
                  {error}
                </p>
              )}

              <div className="mt-[18px] text-center">
                <GhostButton onClick={handleDismiss} data-testid="unlock-dismiss">
                  {COPY.UNLOCK_GATE_DISMISS_CTA}
                </GhostButton>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Shown when the debt is already cleared but the sealed payload has not been
// re-fetched yet — the user judged their tenth portrait in another tab, or the
// server has not re-evaluated. Without `onViewResults` the CTA falls back to
// dismissing, which returns the caller to its own surface.
function ClearedPanel({ onViewResults }) {
  return (
    <div
      className="relative mt-[26px] overflow-hidden rounded-card border border-aurum/55 bg-aurum-faint px-6 py-[30px] text-center"
      data-testid="unlock-cleared"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-10 bg-[conic-gradient(from_0deg,rgba(201,164,92,0)_0deg,rgba(201,164,92,0.3)_14deg,rgba(201,164,92,0)_28deg,rgba(201,164,92,0)_56deg,rgba(201,164,92,0.3)_70deg,rgba(201,164,92,0)_84deg,rgba(201,164,92,0)_140deg,rgba(201,164,92,0.3)_154deg,rgba(201,164,92,0)_168deg,rgba(201,164,92,0)_220deg,rgba(201,164,92,0.3)_234deg,rgba(201,164,92,0)_248deg,rgba(201,164,92,0)_310deg,rgba(201,164,92,0.3)_324deg,rgba(201,164,92,0)_338deg)] motion-safe:animate-[aura-pulse_3.4s_ease-in-out_infinite]"
      />
      <div className="relative">
        <div className="mx-auto flex h-[66px] w-[66px] items-center justify-center rounded-full border-[1.5px] border-aurum bg-obsidian/50 shadow-[0_0_26px_rgba(201,164,92,0.5)]">
          <LuLock className="text-aurum-bright" size={30} strokeWidth={1.8} aria-hidden="true" />
        </div>
        <div className="mt-[18px] font-censor text-[13px] font-semibold uppercase tracking-[0.28em] text-aurum">
          {COPY.UNLOCK_GATE_CLEARED_EYEBROW}
        </div>
        <div className="mt-[10px] font-display text-[26px] text-bone">
          {COPY.UNLOCK_GATE_CLEARED_HEADLINE}
        </div>
        <div className="mt-2 font-display text-[15px] text-bone-dim">
          {COPY.UNLOCK_GATE_CLEARED_BODY}
        </div>
        <div className="mt-[22px]">
          <AurumButton
            variant="plate"
            size="lg"
            fullWidth
            onClick={onViewResults}
            data-testid="unlock-view-results"
          >
            {COPY.UNLOCK_GATE_CLEARED_CTA}
          </AurumButton>
        </div>
      </div>
    </div>
  );
}
